import html2pdf from 'html2pdf.js';
import { toast } from 'sonner';

interface PDFExportOptions {
  selectedSections: string[];
  obraId?: string;
  rdoData?: any;
}

interface ExportTelemetry {
  event: string;
  obraId?: string;
  date?: string;
  engine: string;
  error?: string;
}

class PDFExportService {
  private logTelemetry(data: ExportTelemetry): void {
    console.log('📊 PDF Export Telemetry:', data);
  }

  private async waitForImages(container: Element): Promise<void> {
    const images = container.querySelectorAll('img');
    const imagePromises = Array.from(images).map(img => {
      if (img.complete) return Promise.resolve();
      
      return new Promise<void>((resolve) => {
        img.onload = () => resolve();
        img.onerror = () => resolve(); // Continue even if image fails
        setTimeout(() => resolve(), 3000); // Timeout after 3s
      });
    });
    
    await Promise.all(imagePromises);
  }

  private prepareContentForPDF(selectedSections: string[]): HTMLElement {
    // Find RDO content container
    const rdoContainer = document.querySelector('[data-section="header"]')?.closest('.space-y-1\\.5') as HTMLElement;
    
    if (!rdoContainer) {
      throw new Error('Conteúdo RDO não encontrado na página.');
    }

    // Clone the container
    const clonedContainer = rdoContainer.cloneNode(true) as HTMLElement;
    
    // Filter sections if specified
    if (selectedSections.length > 0) {
      const allSections = clonedContainer.querySelectorAll('[data-section]');
      allSections.forEach(section => {
        const sectionName = section.getAttribute('data-section');
        if (sectionName && !selectedSections.includes(sectionName)) {
          section.remove();
        }
      });
    }

    // Clean up for PDF
    this.cleanupForPDF(clonedContainer);
    
    return clonedContainer;
  }

  private cleanupForPDF(container: HTMLElement): void {
    // Remove interactive elements
    const interactiveSelectors = [
      'button', 
      '.hover\\:',
      '[data-state]',
      '.cursor-pointer',
      '.transition-',
      '.duration-'
    ];
    
    interactiveSelectors.forEach(selector => {
      try {
        const elements = container.querySelectorAll(selector);
        elements.forEach(el => el.remove());
      } catch (e) {
        // Ignore selector errors
      }
    });

    // Fix spacing and layout
    container.style.maxWidth = '100%';
    container.style.margin = '0';
    container.style.padding = '20px';
    container.style.backgroundColor = '#ffffff';
    container.style.color = '#000000';

    // Fix cards and containers
    const cards = container.querySelectorAll('.bg-card, .bg-background');
    cards.forEach(card => {
      (card as HTMLElement).style.backgroundColor = '#ffffff';
      (card as HTMLElement).style.border = '1px solid #e5e7eb';
      (card as HTMLElement).style.borderRadius = '8px';
      (card as HTMLElement).style.marginBottom = '16px';
      (card as HTMLElement).style.pageBreakInside = 'avoid';
    });

    // Fix badges
    const badges = container.querySelectorAll('.inline-flex, [class*="badge"]');
    badges.forEach(badge => {
      (badge as HTMLElement).style.display = 'inline-flex';
      (badge as HTMLElement).style.alignItems = 'center';
      (badge as HTMLElement).style.justifyContent = 'center';
      (badge as HTMLElement).style.textAlign = 'center';
      (badge as HTMLElement).style.padding = '4px 8px';
      (badge as HTMLElement).style.borderRadius = '9999px';
      (badge as HTMLElement).style.fontSize = '12px';
      (badge as HTMLElement).style.fontWeight = '600';
      (badge as HTMLElement).style.border = '1px solid #d1d5db';
      (badge as HTMLElement).style.backgroundColor = '#f3f4f6';
      (badge as HTMLElement).style.color = '#374151';
    });

    // Fix tables
    const tables = container.querySelectorAll('table');
    tables.forEach(table => {
      (table as HTMLElement).style.width = '100%';
      (table as HTMLElement).style.borderCollapse = 'collapse';
      (table as HTMLElement).style.marginBottom = '16px';
    });

    const tableCells = container.querySelectorAll('td, th');
    tableCells.forEach(cell => {
      (cell as HTMLElement).style.border = '1px solid #e5e7eb';
      (cell as HTMLElement).style.padding = '8px';
      (cell as HTMLElement).style.fontSize = '12px';
    });

    // Fix images
    const images = container.querySelectorAll('img');
    images.forEach(img => {
      (img as HTMLElement).style.maxWidth = '100%';
      (img as HTMLElement).style.height = 'auto';
    });

    // Remove scroll containers
    const scrollContainers = container.querySelectorAll('.overflow-auto, .overflow-x-auto, .overflow-y-auto');
    scrollContainers.forEach(scroll => {
      (scroll as HTMLElement).style.overflow = 'visible';
      (scroll as HTMLElement).style.maxHeight = 'none';
    });
  }

  private getHtml2PdfOptions() {
    return {
      margin: [10, 10, 10, 10],
      filename: `RDO_${new Date().toISOString().split('T')[0]}.pdf`,
      image: { 
        type: 'jpeg', 
        quality: 0.95 
      },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        allowTaint: true,
        scrollX: 0,
        scrollY: 0,
        windowWidth: 1200,
        windowHeight: window.innerHeight,
        backgroundColor: '#ffffff'
      },
      jsPDF: { 
        unit: 'mm', 
        format: 'a4', 
        orientation: 'portrait',
        compress: true
      },
      pagebreak: { 
        mode: ['avoid-all', 'css', 'legacy'],
        before: '.page-break-before',
        after: '.page-break-after',
        avoid: '.page-break-avoid'
      }
    };
  }

  async generatePDF(options: PDFExportOptions): Promise<Blob> {
    const telemetryData: ExportTelemetry = {
      event: 'pdf_generation_started',
      obraId: options.obraId,
      date: new Date().toISOString(),
      engine: 'html2pdf.js'
    };

    try {
      this.logTelemetry(telemetryData);
      
      console.log('🔄 Iniciando geração PDF...', options);

      // Prepare content
      const contentElement = this.prepareContentForPDF(options.selectedSections);
      console.log('✅ Conteúdo preparado para PDF');

      // Wait for images
      await this.waitForImages(contentElement);
      console.log('✅ Imagens carregadas');

      // Create temporary container
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'fixed';
      tempContainer.style.top = '-9999px';
      tempContainer.style.left = '-9999px';
      tempContainer.style.width = '1200px';
      tempContainer.style.backgroundColor = '#ffffff';
      tempContainer.appendChild(contentElement);
      document.body.appendChild(tempContainer);

      try {
        // Generate PDF
        const pdfBlob = await html2pdf()
          .set(this.getHtml2PdfOptions())
          .from(contentElement)
          .outputPdf('blob');

        console.log('✅ PDF gerado com sucesso');
        
        this.logTelemetry({
          ...telemetryData,
          event: 'pdf_generation_success'
        });

        return pdfBlob;
      } finally {
        // Clean up
        document.body.removeChild(tempContainer);
      }

    } catch (error) {
      console.error('❌ Erro na geração do PDF:', error);
      
      this.logTelemetry({
        ...telemetryData,
        event: 'pdf_generation_error',
        error: error instanceof Error ? error.message : 'Unknown error'
      });

      throw new Error(`Falha na geração do PDF: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    }
  }

  async exportPDFWithSpinner(options: PDFExportOptions): Promise<void> {
    // Check if RDO content exists
    const rdoSections = document.querySelectorAll('[data-section]');
    if (rdoSections.length === 0) {
      toast.error('Conteúdo RDO não encontrado', {
        description: 'Navegue para a página do RDO antes de exportar.',
        action: {
          label: 'Ir para RDO',
          onClick: () => window.location.href = '/rdo'
        }
      });
      return;
    }

    console.log(`✅ Conteúdo RDO detectado: ${rdoSections.length} seções`);

    const loadingToast = toast.loading('Gerando PDF...', {
      description: 'Preparando conteúdo do relatório...'
    });

    try {
      const pdfBlob = await this.generatePDF(options);
      
      // Download the PDF
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `RDO_${options.obraId || 'export'}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('PDF exportado com sucesso!', {
        description: 'O arquivo foi baixado para seu dispositivo.',
        id: loadingToast
      });

    } catch (error) {
      console.error('Erro na exportação:', error);
      
      toast.error('Erro ao exportar PDF', {
        description: error instanceof Error ? error.message : 'Erro desconhecido na exportação.',
        id: loadingToast,
        action: {
          label: 'Tentar novamente',
          onClick: () => this.exportPDFWithSpinner(options)
        }
      });
    }
  }
}

export const pdfExportService = new PDFExportService();